<template>
  <div class="tc-page">
    <div class="tc-card">
      <div v-if="loading" class="text-center text-muted py-5">{{ confirming ? "Confirming your payment…" : "Loading…" }}</div>

      <div v-else-if="invalid" class="text-center py-5">
        <i class="fe fe-link-2" style="font-size: 2.5rem; opacity: .3;"></i>
        <h1 class="h5 mt-3">This consultation link is not valid</h1>
        <p class="text-muted mb-0">Use the link from your latest email, or contact your health insurance agency.</p>
      </div>

      <template v-else>
        <div class="text-center">
          <img v-if="info.agency && info.agency.logo_url" :src="info.agency.logo_url" :alt="info.agency.name + ' logo'"
            class="tc-logo" @error="$event.target.style.display = 'none'" />
          <p class="tc-eyebrow">{{ info.agency && info.agency.name }}</p>
          <h1 class="tc-title">Video consultation</h1>
        </div>

        <div class="tc-details">
          <div><span>Patient</span><strong>{{ info.patient }}</strong></div>
          <div><span>Doctor</span><strong>{{ info.doctor }}</strong></div>
          <div><span>When</span><strong>{{ formatWhen(info.starts_at) }} – {{ (info.ends_at || '').slice(11) }}</strong></div>
        </div>

        <!-- Independent facilities: the patient pays the consultation fee first (Paystack) -->
        <div v-if="Number(info.fee) > 0" class="tc-pay" :class="{ 'tc-pay--paid': !info.needs_payment }">
          <template v-if="!info.needs_payment">
            <i class="fe fe-check-circle mr-2"></i>Consultation fee of ₦{{ Number(info.fee).toLocaleString("en-NG") }} paid. Thank you.
          </template>
          <template v-else-if="confirming">
            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>Confirming your payment…
          </template>
          <template v-else>
            <p class="mb-2">Consultation fee: <strong>₦{{ Number(info.fee).toLocaleString("en-NG") }}</strong>. Pay now to be able to join.</p>
            <div v-if="!info.patient_email" class="form-group mb-2 text-left">
              <label for="tc-email" class="small font-weight-bold">Your email (for the receipt)</label>
              <input id="tc-email" v-model="email" type="email" class="form-control" autocomplete="email" />
            </div>
            <div v-if="payError" class="small text-danger mb-2">{{ payError }}</div>
            <button type="button" class="btn tc-btn" :disabled="paying || (!info.patient_email && !email)" @click="pay">
              <span v-if="paying" class="spinner-border spinner-border-sm mr-2" role="status"></span>
              <i v-else class="fe fe-credit-card mr-2"></i>Pay with Paystack
            </button>
          </template>
        </div>

        <div v-if="info.can_join_now" class="text-center">
          <p class="small text-muted">Allow your camera and microphone when your browser asks.</p>
          <button type="button" class="btn tc-btn" @click="join"><i class="fe fe-video mr-2"></i>Join the consultation</button>
        </div>
        <div v-else class="tc-wait">
          <i class="fe fe-clock mr-2"></i>{{ info.join_refusal }}
          <div class="small mt-1" v-if="info.join_refusal && info.join_refusal.indexOf('opens') === 0">
            Keep this page: it will let you in from {{ (info.opens_at || '').slice(11) }}.
          </div>
        </div>
        <p class="small text-muted text-center mt-4 mb-0">This link is personal to you. Please don't share it.</p>
      </template>
    </div>
  </div>
</template>

<script>
// Public: the patient's private link from the booking email (/teleconsult/:id?t=…). The API checks the link
// and the time; the call itself is /teleconsult/:id/call (VideoCall in patient mode).
export default {
  data() {
    return { loading: true, invalid: false, info: {}, timer: null, email: "", paying: false, payError: "", confirming: false };
  },
  created() {
    // Back from Paystack (?paid=1): confirm the payment with the API, then show the page.
    if (this.$route.query.paid) this.confirmPayment(0);
    else this.load();
    // While waiting, check again every minute so the button appears when the consultation opens.
    this.timer = setInterval(() => { if (!this.info.can_join_now) this.load(true); }, 60 * 1000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    load(quiet) {
      if (!quiet) this.loading = true;
      this.axios
        .get(`/api/v1/teleconsult/${this.$route.params.id}`, { params: { t: this.$route.query.t } })
        .then((res) => { this.info = res.data; document.title = "Video consultation"; })
        .catch(() => { this.invalid = true; })
        .finally(() => { this.loading = false; });
    },
    formatWhen(value) {
      if (!value) return "";
      const d = new Date(value.replace(" ", "T"));
      return isNaN(d) ? value : d.toLocaleString("en-NG", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });
    },
    pay() {
      this.paying = true;
      this.payError = "";
      this.axios
        .post(`/api/v1/teleconsult/${this.$route.params.id}/pay`, { t: this.$route.query.t, email: this.email || undefined })
        .then((res) => { window.location.href = res.data.authorization_url; })
        .catch((err) => {
          const body = (err.response && err.response.data) || {};
          this.payError = body.errors ? Object.values(body.errors).flat().join(" ") : body.message || "Payment could not be started. Try again.";
          this.paying = false;
        });
    },
    confirmPayment(attempt) {
      this.confirming = true;
      this.axios
        .post(`/api/v1/teleconsult/${this.$route.params.id}/payment-check`, { t: this.$route.query.t })
        .then((res) => {
          // Paystack can take a few seconds to confirm; try a few times before showing the Pay button again.
          if (!res.data.paid && attempt < 4) {
            setTimeout(() => this.confirmPayment(attempt + 1), 3000);
            return;
          }
          this.confirming = false;
          this.load();
        })
        .catch(() => { this.confirming = false; this.load(); });
    },
    join() {
      this.$router.push({ path: `/teleconsult/${this.$route.params.id}/call`, query: { t: this.$route.query.t } });
    },
  },
};
</script>

<style scoped>
.tc-page { min-height: 100vh; background: linear-gradient(160deg, #f5f3ff 0%, #eef2ff 60%, #f8fafc 100%); padding: 40px 16px; }
.tc-card { max-width: 520px; margin: 0 auto; background: #fff; border-radius: 18px; padding: 28px; box-shadow: 0 4px 20px rgba(15,23,42,.08); }
.tc-logo { width: 72px; height: 72px; object-fit: contain; border-radius: 16px; margin-bottom: 10px; }
.tc-eyebrow { text-transform: uppercase; letter-spacing: .08em; font-size: .72rem; font-weight: 700; color: #7c3aed; margin-bottom: 4px; }
.tc-title { font-size: 1.4rem; font-weight: 800; color: #1e1b4b; }
.tc-details { background: #f8fafc; border-radius: 12px; padding: 14px 16px; margin: 18px 0; }
.tc-details div { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; font-size: .92rem; }
.tc-details div + div { border-top: 1px solid #e2e8f0; }
.tc-details span { color: #64748b; }
.tc-details strong { text-align: right; }
.tc-btn { background: linear-gradient(135deg, #4c1d95, #7c3aed); color: #fff; border: none; border-radius: 10px; font-weight: 700; padding: 12px 24px; }
.tc-btn:hover { opacity: .92; color: #fff; }
.tc-pay { background: #f5f3ff; color: #4c1d95; border-radius: 12px; padding: 14px 16px; text-align: center; margin-bottom: 16px; font-size: .92rem; }
.tc-pay--paid { background: #ecfdf5; color: #047857; }
.tc-wait { background: #fef3c7; color: #92400e; border-radius: 10px; padding: 12px 14px; text-align: center; font-size: .92rem; }
</style>
